import { Dispatch, useCallback, useContext, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { decisionSendAgreement } from 'DecisionPackage/MainModule/actions/decisionActions';
import { knmSendAgreement } from 'KnmPackage/MainModule/actions/knmActions';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import {
  failExecutionSelectOptions,
  successExecutionSelectOptions,
} from 'KnmPackage/MainModule/constants/agreementsExecutionSelectOptions';

import { ExecutionAgreementModal } from './ExecutionAgreementModal/ExecutionAgreementModal';
import { TerminationAgreementModal } from './TerminationAgreementModal/TerminationAgreementModal';
import { SelectTypeActionModal } from './SelectTypeActionModal/SelectTypeActionModal';
import { ChangeAgreementModal } from './ChangeAgreementModal/ChangeAgreementModal';

interface IWorkWithAgreementButtonProps {
  name: string;
  isDirtyBySigned: boolean;
  agreement: IErknmFormAgreement;
  areAllViolationCompleted: boolean;
  isBeforeTermOfExecutionAgreement: boolean;
}

interface IWorkWithAgreementButtonStoreProps {
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const WorkWithAgreementButtonWithoutStore = ({
  name,
  agreement,
  isSupervisor,
  isDirtyBySigned,
  areAllViolationCompleted,
  isBeforeTermOfExecutionAgreement,
  decisionFormRef,
  formRefKnm,
  isProsecutorOrSuperProsecutor,
}: IWorkWithAgreementButtonProps & IWorkWithAgreementButtonStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { isDecision } = useContext(AgreementsContext);

  const [isSelectTypeActionModalOpen, setSelectTypeActionIsModalOpen] = useState(false);
  const [isExecutionModalOpen, setExecutionIsModalOpen] = useState(false);
  const [executionModalInitType, setExecutionModalInitType] = useState<ISelectOption<number> | undefined>(undefined);
  const [isTerminationModalOpen, setTerminationIsModalOpen] = useState(false);
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);

  /**
   * Функция расторжения соглашения
   */
  const sendRequestToChangeStatus = useCallback(() => {
    // @ts-ignore
    (isDecision ? decisionFormRef : formRefKnm)?.current.change(`${name}.status`, AgreementStatusEnum.TERMINATED);
    dispatch(isDecision ? decisionSendAgreement() : knmSendAgreement());
  }, [isDecision, formRefKnm, decisionFormRef]);

  /**
   * Список действий, доступных сейчас пользователю с соглашением
   */
  const availableOptions = useMemo(() => {
    const options: ISelectOption<string>[] = [];

    if (isSupervisor) {
      options.push(
        ...[
          {
            id: 'change',
            label: 'Изменить соглашение',
            isDisabled: !(
              isSupervisor &&
              agreement.signed &&
              agreement.status === AgreementStatusEnum.APPROVED &&
              !isDirtyBySigned &&
              isBeforeTermOfExecutionAgreement
            ),
          },
          {
            id: 'terminate',
            label: 'Расторгнуть соглашение',
            isDisabled: !(
              isSupervisor &&
              agreement.status === AgreementStatusEnum.PENDING_TERMINATION &&
              agreement.signed
            ),
          },
          {
            id: 'compelte',
            label: 'Признать соглашение исполненным',
            isDisabled: !areAllViolationCompleted,
          },
        ],
      );
    }

    if (isSupervisor || isProsecutorOrSuperProsecutor) {
      options.push({
        id: 'not_complete',
        label: 'Признать соглашение неисполненным',
        isDisabled: agreement.status === AgreementStatusEnum.PENDING_TERMINATION,
      });
    }

    return options;
  }, [
    isSupervisor,
    isProsecutorOrSuperProsecutor,
    agreement,
    isDirtyBySigned,
    isBeforeTermOfExecutionAgreement,
    areAllViolationCompleted,
  ]);

  /**
   * Объект действий с открытием необходимой модалки
   */
  const actions = useMemo(() => {
    return {
      change: () => setIsChangeModalOpen(true),
      terminate: sendRequestToChangeStatus,
      not_complete: () => {
        if (isBeforeTermOfExecutionAgreement) {
          setTerminationIsModalOpen(true);
        } else {
          setExecutionModalInitType(failExecutionSelectOptions);
          setExecutionIsModalOpen(true);
        }
      },
      complete: () => {
        setExecutionModalInitType(successExecutionSelectOptions);
        setExecutionIsModalOpen(true);
      },
    } as { [key: string]: VoidFunction };
  }, [isBeforeTermOfExecutionAgreement, sendRequestToChangeStatus]);

  /**
   * Функция открытия соответствующей модалки
   */
  const openNextModal = useCallback(
    (id: string) => {
      actions[id]();
    },
    [actions],
  );

  /**
   * Функция закрытия модального окна исполнения/неисполнения соглашения
   */
  const onCloseExecutionModal = useCallback((needCloseAll?: boolean) => {
    if (needCloseAll) {
      setSelectTypeActionIsModalOpen(false);
    }
    setExecutionModalInitType(undefined);
    setExecutionIsModalOpen(false);
  }, []);

  return (
    <>
      <Button
        color="primary"
        text={'Работа с соглашением'}
        id="saveButton"
        type={'button'}
        onClick={() => setSelectTypeActionIsModalOpen(true)}
      />
      <SelectTypeActionModal
        name={name}
        visible={isSelectTypeActionModalOpen}
        onClose={() => setSelectTypeActionIsModalOpen(false)}
        options={availableOptions}
        onSuccess={openNextModal}
        isSupervisor={isSupervisor}
      />
      <ExecutionAgreementModal
        name={name}
        visible={isExecutionModalOpen}
        onClose={onCloseExecutionModal}
        onCloseAll={() => onCloseExecutionModal(true)}
        areAllViolationCompleted={areAllViolationCompleted}
        executionModalInitType={executionModalInitType}
      />
      <TerminationAgreementModal
        name={name}
        prosecutorOrganization={agreement.prosecutorOrganization}
        visible={isTerminationModalOpen}
        onClose={() => setTerminationIsModalOpen(false)}
        onCloseAll={() => {
          setSelectTypeActionIsModalOpen(false);
          setExecutionIsModalOpen(false);
        }}
      />
      <ChangeAgreementModal
        name={name}
        visible={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        onCloseAll={() => {
          setSelectTypeActionIsModalOpen(false);
          setExecutionIsModalOpen(false);
        }}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IWorkWithAgreementButtonStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const WorkWithAgreementButton = connect(mapStateToProps)(WorkWithAgreementButtonWithoutStore);
